/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  addExportConfigurationToServiceType,
  editExportConfiguration,
  getExportConfigurationByServiceType,
  removeExportConfiguration,
  getExportConfigurationTransactionTypes,
} from "@/requests/administration/servicesAndAttributes/administrationServiceTypes";
import {
  ExportConfiguration,
  ExportConfigurationSetting,
  OfficeProfileResult,
  ServiceTypeExportConfiguration,
} from "@/models/api/administration/servicesAndAttributes/ServiceTypeExportConfiguration";
import { NewExportConfiguration } from "@/models/api/administration/servicesAndAttributes/NewExportConfiguration";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import i18n from "@/plugins/i18n";
import { filterCapitalize } from "@/filters/capitalize";
import _ from "lodash/fp";
import { SelectItem } from "@/models/api/SelectItem";

export interface AdministrationServiceTypeExportConfigurationStateInterface {
  serviceTypeId: null | number;
  exportConfigurations: Array<ExportConfiguration>;
  officeProfiles: Array<OfficeProfileResult>;
  exportConfigurationSettings: Array<ExportConfigurationSetting>;
  newExportConfiguration: NewExportConfiguration;
  newExportConfigurationConfigurationSettingIds: Array<number>;
  transactionTypes: Array<SelectItem>;
}

export const AdministrationServiceTypeExportConfigurationState: AdministrationServiceTypeExportConfigurationStateInterface =
  {
    serviceTypeId: null,
    exportConfigurations: [],
    exportConfigurationSettings: [],
    officeProfiles: [],
    newExportConfiguration: new NewExportConfiguration([]),
    newExportConfigurationConfigurationSettingIds: [],
    transactionTypes: [],
  };

const AdministrationServiceTypeExportConfigurationModule = defineModule({
  state:
    AdministrationServiceTypeExportConfigurationState as AdministrationServiceTypeExportConfigurationStateInterface,
  namespaced: true as const,
  getters: {
    defaultServiceDescription(...args): string {
      const { state, rootState } = _moduleGetterContext(args);
      const serviceDescription = rootState.administration.serviceTypes.serviceTypes.find(
        (v) => v.id === state.serviceTypeId
      )?.name;
      return serviceDescription ?? "";
    },
  },
  mutations: {
    setServiceTypeId(state, param: null | number) {
      state.serviceTypeId = param;
    },
    setExportConfiguration(state, param: ServiceTypeExportConfiguration) {
      state.exportConfigurationSettings = param.exportConfigurationSettings;
      state.officeProfiles = param.officeProfiles;
      state.exportConfigurations = param.exportConfigurations;
    },
    setNewExportConfiguration(state, param: NewExportConfiguration) {
      state.newExportConfiguration = param;
    },
    setExportConfigurations(state, param: ExportConfiguration[]) {
      state.exportConfigurations = param;
    },
    setNewExportConfigurationConfigurationSettingIds(state, param: Array<number>) {
      state.newExportConfigurationConfigurationSettingIds = param;
    },
    setTransactionTypes(state, param: Array<SelectItem>) {
      state.transactionTypes = param;
    },
  },
  actions: {
    async openExportConfigurationDialog(context, serviceTypeId: number) {
      const { commit, dispatch, rootDispatch, state, getters } = _moduleActionContext(context);
      commit.setServiceTypeId(serviceTypeId);
      await dispatch.downloadExportConfiguration();
      commit.setNewExportConfiguration(
        new NewExportConfiguration(state.officeProfiles, getters.defaultServiceDescription)
      );
      commit.setNewExportConfigurationConfigurationSettingIds([]);
      // noinspection ES6MissingAwait
      rootDispatch.waitForCompleteAllRequests();
    },
    async downloadExportConfiguration(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.serviceTypeId !== null) {
        const exportConfiguration = await getExportConfigurationByServiceType(state.serviceTypeId);
        for (const config of exportConfiguration.exportConfigurations) {
          config.createTemporaryExportConfigurationOfficeProfiles(exportConfiguration.officeProfiles);
        }
        commit.setExportConfiguration(exportConfiguration);
      }
    },
    async removeExportConfiguration(context, exportConfigurationId: number) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (await rootDispatch.popup.addConfirmRemoveElement()) {
        await removeExportConfiguration(exportConfigurationId);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.downloadExportConfiguration();
      }
    },
    async addExportConfiguration(context) {
      const { commit, state, dispatch, rootDispatch, getters } = _moduleActionContext(context);
      if (state.serviceTypeId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        if (state.newExportConfigurationConfigurationSettingIds.length === 0) {
          rootDispatch.popup.addSnackbar(
            new SnackbarModel(filterCapitalize(i18n.t("selectConfigurationSetting(oneOfCheckbox)")), "error")
          );
        } else {
          state.newExportConfiguration.temporaryExportConfigurationOfficeProfilesToExportConfigurationProfiles();
          const exportConfigurations: Array<NewExportConfiguration> = [];
          for (const configurationId of state.newExportConfigurationConfigurationSettingIds) {
            const newExportConfiguration = _.cloneDeep(state.newExportConfiguration);
            delete newExportConfiguration.temporaryExportConfigurationOfficeProfiles;
            newExportConfiguration.exportConfigurationSettingId = configurationId;
            exportConfigurations.push(newExportConfiguration);
          }
          await addExportConfigurationToServiceType(exportConfigurations, state.serviceTypeId);
          // noinspection ES6MissingAwait
          dispatch.downloadExportConfiguration();
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          commit.setNewExportConfiguration(
            new NewExportConfiguration(state.officeProfiles, getters.defaultServiceDescription)
          );
        }
      }
    },
    async editExportConfiguration(context, exportConfiguration: ExportConfiguration) {
      const { rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      const exportConfigurationToRequest = _.cloneDeep(exportConfiguration);
      exportConfigurationToRequest.temporaryExportConfigurationOfficeProfilesToExportConfigurationProfiles();
      delete exportConfigurationToRequest.temporaryExportConfigurationOfficeProfiles;
      await editExportConfiguration(exportConfigurationToRequest, exportConfiguration.id);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
    },
    async downloadTransactionTypes(context) {
      const { commit } = _moduleActionContext(context);
      const response = await getExportConfigurationTransactionTypes();
      commit.setTransactionTypes(response);
    },
  },
});

export default AdministrationServiceTypeExportConfigurationModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationServiceTypeExportConfigurationModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(
    context,
    AdministrationServiceTypeExportConfigurationModule
  );
